'use client';

import clsx from 'clsx';
import type { ButtonVariantProps, ButtonEventHandler } from '../types';
import ButtonComponent, { ButtonSizes, ButtonVariants, type ButtonLinkProps } from '@pixum/button';
import '../styles.css';
import { type LinkProps, withLinkMasking, type WrappedComponent } from '../../link';
import { isLinkOut, getBaseUrl, handleLinkOutTracking } from 'utils';

/**
 * Wrapper for the @pixum/button component with link masking functionality
 */
const ButtonComponentWithLinkMasking = withLinkMasking<LinkProps & ButtonLinkProps>(
  ButtonComponent as WrappedComponent<ButtonLinkProps>,
);

/**
 * Renders a Button component with link masking and device-specific styling from a link skeleton. If no link is provided
 * it returns null. It returns the @pixum/button component with the masking functionality. It uses the new button
 * component from the design system.
 *
 * @param props - ButtonProps
 * @returns A Button component or null if no link is provided.
 */
export function ButtonWithLinkMasking(props: ButtonVariantProps) {
  const { className, link, variant = ButtonVariants.STRONG, device, size = ButtonSizes.MEDIUM, id, onClick } = props;

  if (!link) {
    return null;
  }

  const isExternal = link.url ? isLinkOut(link.url, getBaseUrl()) : false;
  const trackingClick = isExternal ? () => handleLinkOutTracking(link.url as string) : undefined;
  const handleClick = onClick ?? trackingClick;

  const componentClass = clsx(className, `button-for-${device}`);

  return (
    <ButtonComponentWithLinkMasking
      className={componentClass}
      href={link?.url as string}
      label={link?.mouseOverText || link?.title}
      title={link?.title}
      masked={link?.masked}
      type="button"
      variant={variant}
      size={size}
      id={id}
      onClick={handleClick as ButtonEventHandler}
      dataLink={link?.url as string}
    >
      {link?.title}
    </ButtonComponentWithLinkMasking>
  );
}
